import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils.js";

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
}

export function TagInput({ tags, onChange, placeholder = "Add tag...", suggestions = [] }: TagInputProps) {
  const [input, setInput] = React.useState("");
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const addTag = (value: string) => {
    const tag = value.trim().toLowerCase();
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput("");
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === ",") && input.trim()) {
      e.preventDefault();
      addTag(input);
    }
    if (e.key === "Backspace" && !input && tags.length) {
      onChange(tags.slice(0, -1));
    }
    if (e.key === "Escape") setShowSuggestions(false);
  };

  const filtered = input.trim()
    ? suggestions.filter((s) => s.includes(input.trim().toLowerCase()) && !tags.includes(s)).slice(0, 8)
    : suggestions.filter((s) => !tags.includes(s)).slice(0, 8);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex flex-wrap gap-1.5 p-2 rounded-xl border border-neutral-border/40 focus-within:border-primary min-h-[42px]">
        {tags.map((tag) => (
          <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => { if (input.trim()) setTimeout(() => addTag(input), 150); }}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] text-sm outline-none bg-transparent"
        />
      </div>
      {showSuggestions && filtered.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-neutral-border/40 rounded-xl shadow-lg max-h-40 overflow-y-auto">
          {filtered.map((s) => (
            <button key={s} type="button" onMouseDown={(e) => { e.preventDefault(); addTag(s); }} className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 text-gray-700">{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
